import { Component, OnInit, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { BankStore, ContactStore } from '../../core/stores/base.store';
import { formatDateAsYYYYMMDD } from 'app/core/utils/date.utils';
import { NumberFormatDirective } from '../../core/utils/comma.utils';
import { LoanUtils } from '../../core/utils/loan.utils';
import { NotificationUtils } from '../../core/utils';

@Component({
  selector: 'app-bank-deposit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSelectModule,
    NumberFormatDirective
  ],
  templateUrl: './bank-deposit-dialog.component.html',
  styleUrls: ['./bank-deposit-dialog.component.css']
})
export class BankDepositDialogComponent implements OnInit {
  depositForm!: FormGroup;
  
  private bankStore = inject(BankStore);
  private contactStore = inject(ContactStore);
  private fb = inject(FormBuilder);
  
  selectedBank = this.bankStore.selected;

  allCategories = ['Anuity', 'Income', 'Investment', 'Rental', 'Retirement', 'Other'];

  // Get all contacts
  allContacts = this.contactStore.entities;
  
  // Filtered contacts for income sources (category = 'Income')
  contacts = computed(() => {
    return this.allContacts().filter(contact => contact.category === 'Income');
  });

  selfContact = computed(() => {
    return this.allContacts().find(contact => contact.category === 'Self');
  });
  
  constructor(
    public dialogRef: MatDialogRef<BankDepositDialogComponent>
  ) {}
  
  ngOnInit(): void {
    this.depositForm = this.fb.group({
      amount: ['', [Validators.required, Validators.min(0.01)]],
      date: [new Date(), Validators.required],
      category: ['', Validators.required],
      contact_id: [null, Validators.required],
    });
  }
  
  onSubmit(): void {
    if (this.depositForm.invalid) {
      return;
    }
    
    const formValue = this.depositForm.value;
    const bank = this.selectedBank();
    
    if (!bank) {
      NotificationUtils.getInstance().showError('No bank selected');
      return;
    }
    
    // Create the deposit transaction
    const depositTransaction = {
      bank_id: bank.id,
      transaction_date: formatDateAsYYYYMMDD(formValue.date),
      amount: Math.abs(LoanUtils.parseFormattedNumber(formValue.amount)),
      category: formValue.category,
      contact_id: formValue.contact_id || null,
      loan_id: null,
      insurance_id: null,
      insurance_loan_id: null,
      principal: null,
      interest: null,
      late_fee: null
    };
    
    try {
      // Save the deposit transaction
      this.bankStore.createChild({ 
        child: depositTransaction, 
        endpoint: 'bank_transaction', 
        collectionName: 'bank_transactions_collection' 
      });
      
      NotificationUtils.getInstance().showSuccess('Deposit recorded successfully');
      this.dialogRef.close(true);
    } catch (error) {
      NotificationUtils.getInstance().showError('Error recording deposit');
      console.error('Error creating deposit transaction:', error);
    }
  }
  
  onCancel(): void {
    this.dialogRef.close(false);
  }
}
